import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setToken } from "@/lib/api";
import { setCustomCategories } from "@/lib/lore";
import { bindUser } from "@/lib/offline/engine";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const userId = useRef(null);

  const resetSession = useCallback(async () => {
    await qc.cancelQueries();
    qc.clear();
    setCustomCategories([]);
  }, [qc]);

  const applyUser = useCallback(async (u) => {
    if ((u?.id || null) !== userId.current) await resetSession();
    userId.current = u?.id || null;
    if (u?.id) await bindUser(u.id);
    setUser(u || false);
  }, [resetSession]);

  useEffect(() => {
    let tries = 0, timer;
    const load = () => api.get("/auth/me").then((r) => { setReconnecting(false); applyUser(r.data); }).catch((e) => {
      const st = e?.response?.status;
      if (st === 401 || st === 403 || tries >= 20) { setReconnecting(false); applyUser(false); return; }
      tries += 1;
      setReconnecting(true);
      timer = setTimeout(load, Math.min(1000 * 2 ** Math.min(tries, 4), 15000));
    });
    load();
    const onLogout = () => { setToken(null); applyUser(false); };
    const onStorage = (e) => { if (e.key === "lf_token") window.location.reload(); };
    window.addEventListener("lf-logout", onLogout);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("lf-logout", onLogout);
      window.removeEventListener("storage", onStorage);
      clearTimeout(timer);
    };
  }, [applyUser]);

  const signIn = useCallback(async (path, body) => {
    await resetSession();
    const { data } = await api.post(path, body);
    setToken(data.access_token);
    await applyUser(data.user);
  }, [applyUser, resetSession]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setToken(null);
    await resetSession();
    userId.current = null;
    setUser(false);
  }, [resetSession]);

  const reload = useCallback(() => api.get("/auth/me").then((r) => setUser(r.data)), []);

  return <AuthContext.Provider value={{ user, signIn, logout, reload }}>
    {reconnecting && user === null && <div className="fixed inset-x-0 top-0 z-[100] bg-[#1A1508] py-2 text-center text-xs text-[#FBBF24]" data-testid="reconnecting-banner">Reconnecting to LoreRight… you're still signed in.</div>}
    {children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
